import {
  StreamingDeviceListAction,
  KalturaStreamingDeviceFilter,
  KalturaStreamingDeviceListResponse,
} from '@kaltura-ott/ott-client-types/api/types';
import {
  HouseholdGetAction,
  HouseholdDeviceAddAction,
  HouseholdDeviceGetAction,
  HouseholdDeviceListAction,
  HouseholdDeviceDeleteAction,
  KalturaHouseholdDevice,
  HouseholdPaymentGatewayListAction,
  HouseholdPaymentGatewayInvokeAction,
  KalturaHouseholdDeviceArgs,
} from '@kaltura-ott/ott-client-types/api/types';
import { KalturaHouseholdDeviceListResponse } from '@kaltura-ott/ott-client-types/api/types/KalturaHouseholdDeviceListResponse';
import { KalturaKeyValue } from '@kaltura-ott/ott-client-types/api/types/KalturaKeyValue';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HouseholdStreamingDevices } from '../interfaces';

import { deviceBrandGetList } from './device-brand';
import { mergeDevicesWithStreamingDevices } from './household.helper';
import { kalturaClient } from './kaltura-clent';
/**
 * Build request payload for service 'Household' action 'DeviceGet'.
 *
 * Usage: get device data
 *
 * @category HouseholdManagement
 * @function
 */
export const householdGetDevice = () => {
  const client = kalturaClient();
  const action = new HouseholdDeviceGetAction();
  return client.request(action).pipe(
    catchError((error) => {
      console.warn(error);
      return throwError(error);
    }),
  );
};

/**
 * Build request payload for service 'Household' action 'DeviceAdd'.
 *
 * Usage: get device data
 *
 * @category HouseholdManagement
 * @function
 */
export const householdAddDevice = (deviceData: KalturaHouseholdDeviceArgs) => {
  const device = new KalturaHouseholdDevice(deviceData);
  const action = new HouseholdDeviceAddAction({ device });
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      console.trace(error);
      return throwError(error);
    }),
  );
};

/**
 * Build request payload for service 'Household' action 'DeviceListAction'.
 *
 * Usage: get device list
 *
 * @category HouseholdManagement
 * @function
 */
export const householdGetDeviceList = (): Observable<KalturaHouseholdDeviceListResponse> => {
  const action = new HouseholdDeviceListAction();
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      console.trace(error);
      return throwError(error);
    }),
  );
};

/**
 * Build request payload for service 'Household' action 'DeviceDeleteAction'.
 *
 * Usage: remove device
 *
 * @category HouseholdManagement
 * @function
 */
export const householdRemoveDevice = (udid: string) => {
  const action = new HouseholdDeviceDeleteAction({ udid });
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      console.trace(error);
      return throwError(() => new Error(error));
    }),
  );
};

/**
 * Build request payload for service 'Household' action 'PaymentGatewayList'.
 *
 * Usage: get PaymentGatewayList
 *
 * @category Purchase
 * @function
 */
export const householdGetPaymentGateways = () => {
  const action = new HouseholdPaymentGatewayListAction();
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      console.trace(error);
      return throwError(error);
    }),
  );
};

/**
 * Build request payload for service 'Household' action 'PaymentGatewayInvoke'.
 *
 * Usage: invoke PaymentGateway
 *
 * @category Purchase
 * @function
 */
export const householdInvokePaymentGateways = (paymentGatewayId: number) => {
  const action = new HouseholdPaymentGatewayInvokeAction({
    paymentGatewayId,
    intent: 'Config',
    extraParameters: [new KalturaKeyValue()],
  });
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      console.trace(error);
      return throwError(error);
    }),
  );
};

/**
 * Build request payload for service 'Household' action 'HouseholdGet'.
 *
 * Usage: get household data
 *
 * @category getHousehold
 * @function
 */

export const getHousehold = () => {
  const action = new HouseholdGetAction();
  const client = kalturaClient();

  return client.request(action).pipe(
    catchError((error) => {
      console.trace(error);
      return throwError(error);
    }),
  );
};

/**
 * Build request payload for service ' StreamingDevice' action 'List'.
 *
 * Usage: get household data
 *
 * @category getHousehold
 * @function
 */

export const getStreamingDeviceList = (): Observable<KalturaStreamingDeviceListResponse> => {
  const action = new StreamingDeviceListAction({
    filter: new KalturaStreamingDeviceFilter(),
  });
  const client = kalturaClient();

  return client.request(action).pipe(
    catchError((error) => {
      console.trace(error);
      return throwError(error);
    }),
  );
};

/**
 *
 * Usage: get household device list with a streaming devices and brands
 *
 * @category getCompaundHouseholdDeviceList
 * @function
 */

export const getCompoundHouseholdStreamingDeviceList = () => {
  return forkJoin({
    deviceList: householdGetDeviceList(),
    streamingDeviceList: getStreamingDeviceList(),
    deviceBrands: deviceBrandGetList(),
  }).pipe(
    map(({ deviceList, streamingDeviceList, deviceBrands }) => {
      const streamingDeviceIdsSet = new Set();
      streamingDeviceList?.objects.forEach((streamingDevice) => streamingDeviceIdsSet.add(streamingDevice.udid));
      const deviceBrandsMap: Map<number, string> = new Map();
      deviceBrands?.forEach((brand) => deviceBrandsMap.set(brand.id, brand.name));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const items: (KalturaHouseholdDevice & any)[] = [];
      deviceList?.objects.forEach((device) => {
        if (streamingDeviceIdsSet.has(device.udid)) {
          items.push({ ...device, deviceBrandName: deviceBrandsMap.get(+device.brandId) });
        }
      });
      return {
        totalCount: streamingDeviceList?.totalCount,
        items,
      };
    }),
    catchError((error) => {
      console.trace(error);
      return throwError(error);
    }),
  );
};

/**
 * TODO need to add return type
 *
 * Usage: get household device list with a streaming devices
 *
 * @category getCompoundHouseholdDeviceList
 * @function
 */

export const householdGetDeviceListWithStreams = (): Observable<HouseholdStreamingDevices> => {
  return forkJoin({
    deviceList: householdGetDeviceList(),
    streamingDeviceList: getStreamingDeviceList(),
  }).pipe(
    map(({ deviceList, streamingDeviceList }) => {
      return mergeDevicesWithStreamingDevices(deviceList, streamingDeviceList);
    }),
    catchError((error) => {
      console.trace(error);
      return throwError(error);
    }),
  );
};
